/**
 * Team Inbox — Canned-message MANAGER (admin CRUD).
 *
 * TEAMINBOX_SEND_PICKER_BUILD_SPEC §6: list grouped by category (title, EN+हिं
 * preview, auto-detected blanks, visibility) + a +New/Edit editor (Title,
 * Category, English message, हिन्दी संदेश, auto-detected {{token}} chips,
 * Visibility). Saved instantly via onSave — no Meta approval. Also offers a
 * one-click "Load starter library" seed when the store is empty.
 *
 * Replaces the old slash/text-only canned section in InboxSettings.
 */
import { useMemo, useState } from 'react';
import type { CannedReply } from '../lib/types';
import {
  cannedEn,
  cannedHi,
  cannedId,
  cannedTitle,
  cannedVars,
  humanize,
  isNameTok,
} from '../lib/sendPicker';
import {
  CANNED_CATEGORIES,
  CANNED_SEED,
  seedToCanned,
} from '../lib/cannedSeed';

interface EditState {
  id: string | null; // null = new
  title: string;
  category: string;
  en: string;
  hi: string;
  visibility: 'shared' | 'me';
}

const EMPTY_EDIT: EditState = {
  id: null,
  title: '',
  category: CANNED_CATEGORIES[0] || 'Other',
  en: '',
  hi: '',
  visibility: 'shared',
};

/** Categories that exist in the store + the seed set (deduped, ordered). */
function allCategories(items: CannedReply[]): string[] {
  const set = new Set<string>(CANNED_CATEGORIES);
  for (const c of items) if (c.category) set.add(c.category);
  return Array.from(set);
}

function detect(en: string, hi: string): string[] {
  return cannedVars(en, hi);
}

export function CannedManager({
  items,
  myStaffId,
  saving,
  onSave,
}: {
  items: CannedReply[];
  myStaffId: string | null;
  saving: boolean;
  /** Persist the WHOLE canned array (the store contract). Resolves true on ok. */
  onSave: (next: CannedReply[]) => Promise<boolean>;
}) {
  const [catFilter, setCatFilter] = useState('');
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<EditState | null>(null);
  const [busy, setBusy] = useState(false);

  const cats = useMemo(() => allCategories(items), [items]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (catFilter && c.category !== catFilter) return false;
      if (!q) return true;
      return `${cannedTitle(c)} ${cannedEn(c)} ${cannedHi(c)}`.toLowerCase().includes(q);
    });
  }, [items, catFilter, search]);

  function openNew() {
    setEdit({ ...EMPTY_EDIT, category: cats[0] || 'Other' });
  }
  function openEdit(c: CannedReply) {
    setEdit({
      id: cannedId(c),
      title: cannedTitle(c),
      category: c.category || cats[0] || 'Other',
      en: cannedEn(c),
      hi: cannedHi(c),
      visibility: c.visibility === 'me' ? 'me' : 'shared',
    });
  }

  async function saveEdit() {
    if (!edit) return;
    if (!edit.title.trim() || !edit.en.trim()) return;
    setBusy(true);
    const vars = detect(edit.en, edit.hi);
    const id = edit.id || `c_${Date.now()}`;
    const record: CannedReply = {
      id,
      slash: `/${id.toLowerCase().replace(/[^a-z0-9]/g, '')}`,
      title: edit.title.trim(),
      category: edit.category,
      text: edit.en.trim(),
      textEn: edit.en.trim(),
      textHi: edit.hi.trim(),
      vars,
      visibility: edit.visibility,
      ownerUid: edit.visibility === 'me' ? myStaffId : null,
      numberScope: null,
    };
    const next = edit.id
      ? items.map((c) => (cannedId(c) === edit.id ? record : c))
      : [...items, record];
    const ok = await onSave(next);
    setBusy(false);
    if (ok) setEdit(null);
  }

  async function del(c: CannedReply) {
    if (!window.confirm(`Delete "${cannedTitle(c)}"?`)) return;
    setBusy(true);
    await onSave(items.filter((x) => cannedId(x) !== cannedId(c)));
    setBusy(false);
  }

  async function loadStarter() {
    if (!window.confirm('Load the 30 starter canned messages (English + हिन्दी)?')) return;
    setBusy(true);
    const seeded = CANNED_SEED.map((s) => seedToCanned(s, detect));
    await onSave(seeded);
    setBusy(false);
  }

  const detected = edit ? detect(edit.en, edit.hi) : [];

  return (
    <div className="ti-cm" data-testid="ti-canned-manager">
      <div className="ti-cm-head">
        <div>
          <h3>Canned messages</h3>
          <p>
            Quick replies your team can drop into any chat inside the 24-hour window.
            Created right here — no WhatsApp approval needed, available instantly.
          </p>
        </div>
        <button type="button" className="ti-cm-new" onClick={openNew} disabled={saving || busy}>
          + New canned message
        </button>
      </div>

      <div className="ti-cm-toolbar">
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} aria-label="Filter category">
          <option value="">All categories</option>
          {cats.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          aria-label="Search canned messages"
        />
        <span className="ti-cm-count">
          {rows.length} message{rows.length !== 1 ? 's' : ''}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="ti-cm-empty">
          No canned messages yet.{' '}
          <button type="button" className="ti-cm-seed" onClick={loadStarter} disabled={busy || saving}>
            Load the 30 starter messages
          </button>{' '}
          (English + हिन्दी), or add your own.
        </div>
      ) : (
        <table className="ti-cm-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Preview</th>
              <th>Blanks</th>
              <th>Visible to</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const vars = cannedVars(cannedEn(c), cannedHi(c));
              return (
                <tr key={cannedId(c)}>
                  <td>
                    <div className="ti-cm-ttl">{cannedTitle(c)}</div>
                    <div className="ti-cm-id">{cannedId(c)}</div>
                  </td>
                  <td className="ti-cm-cat">{c.category || '—'}</td>
                  <td>
                    <div className="ti-cm-prev2">
                      <b>EN:</b> {previewWith(cannedEn(c))}
                      {cannedHi(c) ? (
                        <>
                          <br />
                          <b>हिं:</b> {previewWith(cannedHi(c))}
                        </>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="ti-cm-vars">
                      {vars.length ? (
                        vars.map((v) => (
                          <span key={v} className="ti-cm-varchip">
                            {v}
                          </span>
                        ))
                      ) : (
                        <span className="ti-cm-dash">—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`ti-cm-vis ${c.visibility === 'me' ? 'me' : 'team'}`}>
                      {c.visibility === 'me' ? 'Just me' : 'Whole team'}
                    </span>
                  </td>
                  <td>
                    <div className="ti-cm-rowbtns">
                      <button type="button" onClick={() => openEdit(c)} disabled={busy}>
                        Edit
                      </button>
                      <button type="button" className="del" onClick={() => del(c)} disabled={busy}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="ti-cm-nomatch">
                  No messages match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* editor overlay */}
      {edit && (
        <div className="ti-cm-edmask" role="dialog" aria-modal="true" aria-label="Edit canned message">
          <div className="ti-cm-editor">
            <div className="ti-cm-ed-h">
              <h3>{edit.id ? 'Edit canned message' : 'New canned message'}</h3>
              <button type="button" className="ti-sp-x" aria-label="Close" onClick={() => setEdit(null)}>
                ✕
              </button>
            </div>
            <div className="ti-cm-ed-body">
              <div className="ti-cm-ed-field">
                <label>Title (only your team sees this)</label>
                <input
                  value={edit.title}
                  onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  placeholder="e.g. Gentle payment reminder"
                  aria-label="Title"
                />
              </div>
              <div className="ti-cm-ed-field">
                <label>Category</label>
                <select
                  value={edit.category}
                  onChange={(e) => setEdit({ ...edit, category: e.target.value })}
                  aria-label="Category"
                >
                  {cats.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ti-cm-ed-field full">
                <label>English message</label>
                <textarea
                  value={edit.en}
                  onChange={(e) => setEdit({ ...edit, en: e.target.value })}
                  placeholder="Type the English version… use {{first}} for the name, {{service}} etc."
                  aria-label="English message"
                />
                <div className="ti-cm-ed-hint">
                  Tip: type a blank as <b>{'{{first}}'}</b>, <b>{'{{service}}'}</b>, <b>{'{{date}}'}</b> —
                  staff fill these in when sending.
                </div>
              </div>
              <div className="ti-cm-ed-field full">
                <label>हिन्दी संदेश (Hindi — Devanagari)</label>
                <textarea
                  value={edit.hi}
                  onChange={(e) => setEdit({ ...edit, hi: e.target.value })}
                  placeholder="हिन्दी संस्करण यहाँ लिखें… नाम के लिए {{first}} का प्रयोग करें"
                  aria-label="Hindi message"
                />
              </div>
              <div className="ti-cm-ed-field full">
                <label>Blanks detected automatically</label>
                <div className="ti-cm-detected">
                  {detected.length ? (
                    detected.map((v) => (
                      <span key={v} className="ti-cm-varchip">
                        {v}
                        {isNameTok(v) ? ' (name · optional)' : ''}
                      </span>
                    ))
                  ) : (
                    <span className="ti-cm-dash">— none yet —</span>
                  )}
                </div>
              </div>
              <div className="ti-cm-ed-field">
                <label>Visible to</label>
                <select
                  value={edit.visibility}
                  onChange={(e) => setEdit({ ...edit, visibility: e.target.value as 'shared' | 'me' })}
                  aria-label="Visibility"
                >
                  <option value="shared">Whole team (shared library)</option>
                  <option value="me">Just me (personal)</option>
                </select>
              </div>
              <div className="ti-cm-ed-field">
                <label>&nbsp;</label>
                <div className="ti-cm-ed-hint">
                  Saved instantly. No WhatsApp/Meta approval needed — canned messages aren't templates.
                </div>
              </div>
            </div>
            <div className="ti-cm-ed-foot">
              <span className="ti-cm-ed-note">
                Both English &amp; हिन्दी shown to staff; they pick a language when sending.
              </span>
              <div className="ti-sp-foot-btns">
                <button type="button" className="ti-sp-btn" onClick={() => setEdit(null)} disabled={busy}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ti-sp-btn primary"
                  onClick={() => void saveEdit()}
                  disabled={busy || !edit.title.trim() || !edit.en.trim()}
                >
                  {busy ? 'Saving…' : 'Save message'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** One-line preview replacing {{token}} with ‹Token›. */
function previewWith(body: string): string {
  return body.replace(/\{\{(\w+)\}\}/g, (_m, tok) => `‹${humanize(tok)}›`);
}
